import EmptyFolder from 'assets/img/app/empty-folder.png';
import { IonContent, IonDatetime, IonIcon, IonPage, IonSpinner } from "@ionic/react"
import { ImageVisualizer, ModalBackground, SidebarMenu, SolidButton } from "components";
import { useEffect, useRef, useState } from "react";
import { formatDateToISODateString, formatDateToLocaleDateString, formatISODateStringToDate } from "utils";
import { Accordion, AccordionDetails, AccordionSummary, Checkbox, FormControlLabel, Pagination } from '@mui/material';
import { caretDown } from 'ionicons/icons';
import axios from 'axios';
import { useAppSelector } from 'hooks';


export const OrdersPage = () => {
    const { token } = useAppSelector(state => state.user);
    const today = new Date();
    const todayAux = new Date();
    const recordsPerView = 10;

    // estado para almacenar los benchmarks recuperados
    const [loading, setLoading] = useState<boolean>(false);

    // estados para almacenar los datos del reporte
    const [reports, setReports] = useState<any[][]>([]);
    const [backupReports, setBackupReports] = useState<any[]>([]);

    // estados para manejar la paginación
    const [paginationCount, setPaginationCount] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);

    // estados para manejar las pantallas de carga

    // estado que almacena el inicio del rango de fechas de los benchmarks consultadas
    const [startDate, setStartDate] = useState<Date>(new Date(todayAux.setDate(todayAux.getDate() - 30)));
    const [finishDate, setFinishDate] = useState<Date>(new Date());

    // estado para almacenar el arreglo de imágenes que se mostrarán en pantalla
    const [imagesToShow, setImagesToShow] = useState<string[]>([]);
    const [showImageModal, setShowImageModal] = useState<boolean>(false);

    // estados para almacenar los valores que aparecerán en los filtros de búsqueda
    const [searchFilters, setSearchFilters] = useState<{ user: any[], sucursal: any[] }>({ user: [], sucursal: [] });

    const contentRef = useRef<HTMLIonContentElement>(null);

    const getBenchmarkDocs = async (signal: AbortSignal) => {
        setLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_SERVICE_URL}/benchmark/get/report`, {
            params: {
                startDate: `${startDate.getFullYear()}-${(startDate.getMonth() + 1).toString().padStart(2, '0')}-${startDate.getDate().toString().padStart(2, '0')}`,
                endDate: `${finishDate.getFullYear()}-${(finishDate.getMonth() + 1).toString().padStart(2, '0')}-${finishDate.getDate().toString().padStart(2, '0')}`,
            },
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `UserToken ${token}`
            },
            signal: signal
        })

        const benchmarksData = response.data?.data?.orders || [];

        setPaginationCount(Math.ceil(benchmarksData.length / recordsPerView));
        setCurrentPage(1);
        setReports(benchmarksData)
        setBackupReports(benchmarksData)

        setSearchFilters(() => {
            let user = response.data?.data?.accounts || [];
            let sucursal = response.data?.data?.sucursals || [];
            user = user.map((account: any) => ({ ...account, checked: true }))
            sucursal = sucursal.map((suc: any) => ({ ...suc, checked: true }))
            return {
                sucursal: [{
                    key: 'all',
                    name: 'Todas las sucursales',
                    checked: true
                }, ...sucursal],
                user: [{
                    key: 'all',
                    name: 'Todos los displays',
                    lastName: '',
                    checked: true
                }, ...user]
            }
        })
        setLoading(false);
    }

    useEffect(() => {
        const controller = new AbortController();
        getBenchmarkDocs(controller.signal);
        return () => {
            controller.abort();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const changePaginationHandler = async (event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
        if (contentRef.current) {
            contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    const showImageHandler = (images: string[]) => {
        setImagesToShow(images);
        setShowImageModal(true);
    }

    const changeSearchFilterHandler = (item: any, filterType: "user" | "sucursal") => {
        console.log(item, filterType);

        setSearchFilters((prevFilters) => {
            let updatedFilterItems = prevFilters[filterType].map((filterItem: any) => {
                if (filterItem.key === item.key) {
                    return { ...filterItem, checked: !filterItem.checked };
                }
                return filterItem;
            });

            if (item.key === 'all') {
                updatedFilterItems = updatedFilterItems.map((filterItem: any) => ({ ...filterItem, checked: !item.checked }));
                setReports(() => item.checked ? [] : backupReports);
            } else {
                setReports(() => {
                    const filteredReports = backupReports.filter((r: any) => {
                        const filterItem = updatedFilterItems.find((fi: any) => fi.key === r[filterType === 'user' ? 'displayKey' : 'sucursalId']);
                        return filterItem ? filterItem.checked : false;
                    });
                    return filteredReports;
                })
            }
            return {
                ...prevFilters,
                [filterType]: updatedFilterItems
            };
        })
    }

    return (
        <IonPage>
            <IonContent ref={contentRef}>
                <div className="w-full h-screen grid grid-cols-12">
                    {/* Menú lateral */}
                    <div className="col-span-2 border-r-2 border-light">
                        <SidebarMenu pageName="orders" />
                    </div>

                    <div className="w-full h-screen flex flex-col flex-grow col-span-10">
                        <div className="w-full flex justify-between border-b-2 border-tertiary px-2 py-3">
                            <p className="text-primary font-bold text-lg">Reporte de Pedidos</p>

                            <p className="font-semibold text-lg">Del {formatDateToLocaleDateString(startDate)} al {formatDateToLocaleDateString(finishDate)}</p>
                        </div>

                        <div id="main-section" className="w-full h-screen overflow-y-scroll grid grid-cols-10">
                            {
                                loading ?
                                    <div className="col-span-10 w-full h-full flex flex-col items-center justify-center">
                                        <IonSpinner name="crescent" color="primary" className="w-[50px] h-[50px]" />
                                    </div>
                                    :
                                    <div className="w-full flex flex-col flex-grow col-span-7">
                                        {
                                            reports.length === 0 ?
                                                <div className="h-screen grid grid-cols-4">
                                                    <div className="col-span-4 flex flex-col items-center gap-4 p-4">
                                                        <img src={EmptyFolder} alt='empty' className="h-[200px] object-contain" />
                                                        <p className="text-xl text-center font-semibold text-primary">No se encontraron resultados para tu búsqueda</p>
                                                    </div>
                                                </div>
                                                :
                                                <div className="w-full h-full flex flex-col">
                                                    <div className="px-2 pt-4 pb-1 grid grid-cols-4 gap-4 text-mid border-b-2 border-tertiary [&>p]:mt-auto [&>p]:text-primary [&>p]:font-semibold">
                                                        <p>Display</p>
                                                        <p>Fecha</p>
                                                        <p>Sucursal</p>
                                                        <p>Imagen</p>
                                                    </div>

                                                    <div className="w-full flex flex-col">
                                                        {
                                                            reports.map((item: any, i: number) => (
                                                                i >= (recordsPerView * currentPage - recordsPerView) && i < (recordsPerView * currentPage) ?
                                                                    <div key={i} className='w-full p-2 bg-white odd:bg-light cursor:pointer [&>div]:text-ellipsis [&>div]:overflow-hidden [&>div]:... [&>div]:p-2 text-sm text-gray-700 grid grid-cols-4'>
                                                                        <div> {item.name} </div>
                                                                        <div> {item.created} </div>
                                                                        <div> {item.nameSucursal} </div>
                                                                        {
                                                                            item.image !== null &&
                                                                            <div>
                                                                                <button
                                                                                    className='bg-tertiary hover:opacity-80 font-semibold px-2 py-1 rounded-lg text-small'
                                                                                    onClick={() => showImageHandler([item.image])}>Ver imagen
                                                                                </button>
                                                                            </div>
                                                                        }
                                                                    </div>
                                                                    :
                                                                    null
                                                            ))
                                                        }
                                                    </div>
                                                </div>
                                        }

                                        <div className="w-full flex justify-center mt-auto py-4">
                                            <Pagination
                                                size="large"
                                                color="primary"
                                                count={paginationCount}
                                                defaultPage={1}
                                                page={currentPage}
                                                siblingCount={1}
                                                boundaryCount={2}
                                                onChange={changePaginationHandler}
                                            />
                                        </div>
                                    </div>
                            }

                            {/* Filtros de búsqueda */}
                            {
                                !loading &&
                                <div className="w-full overflow-y-scroll flex p-2 flex-col col-span-3 order-last border-l-2 border-light">
                                    <Accordion className="accordion-search-filter">
                                        <AccordionSummary
                                            expandIcon={<IonIcon icon={caretDown} className="text-[18px] text-black" />}
                                            aria-controls="panel1a-content"
                                            id="panel1a-header"
                                        >
                                            <p className="font-semibold text-mid">Fecha</p>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <div className="flex flex-col items-center gap-12">
                                                <div className="flex flex-col gap-2">
                                                    <p className="font-semibold">Fecha de inicio</p>
                                                    <IonDatetime
                                                        presentation="date"
                                                        value={formatDateToISODateString(startDate)}
                                                        max={formatDateToISODateString(today)}
                                                        onIonChange={(e: any) => {
                                                            const selectedDate = e.detail.value ? formatISODateStringToDate(e.detail.value, false) : new Date();
                                                            setStartDate(selectedDate ?? new Date());
                                                        }}
                                                    />
                                                </div>
                                                <div className="flex flex-col gap-2">
                                                    <p className="font-semibold">Fecha de fin</p>
                                                    <IonDatetime
                                                        presentation="date"
                                                        value={formatDateToISODateString(finishDate)}
                                                        max={formatDateToISODateString(today)}
                                                        onIonChange={(e: any) => {
                                                            const selectedDate = e.detail.value ? formatISODateStringToDate(e.detail.value, false) : new Date();
                                                            setFinishDate(selectedDate ?? new Date());
                                                        }}
                                                    />
                                                </div>
                                                <div className="w-full xl:w-1/2">
                                                    <SolidButton
                                                        text="Aplicar"
                                                        backgroundColor="primary"
                                                        disabled={false}
                                                        onClickHandler={() => {
                                                            const controller = new AbortController();
                                                            getBenchmarkDocs(controller.signal);
                                                            return () => controller.abort();
                                                        }} />
                                                </div>
                                            </div>
                                        </AccordionDetails>
                                    </Accordion>

                                    <Accordion className="accordion-search-filter">
                                        <AccordionSummary
                                            expandIcon={<IonIcon icon={caretDown} className="text-[18px] text-black" />}
                                            aria-controls="panel1a-content"
                                            id="panel1a-header"
                                        >
                                            <p className="font-semibold text-mid">Displays</p>
                                        </AccordionSummary>

                                        <AccordionDetails>
                                            <div className="flex flex-col">
                                                {
                                                    searchFilters.user.length === 0 ?
                                                        <p className="font-semibold text-primary">No hay datos disponibles</p>
                                                        :
                                                        searchFilters.user.map((item: any, i: number) => {
                                                            return (
                                                                <FormControlLabel
                                                                    key={item.key}
                                                                    label={item.name + " " + item.lastName}
                                                                    control=
                                                                    {
                                                                        <Checkbox
                                                                            id={item.key}
                                                                            name={item.name + " " + item.lastName}
                                                                            value={item.key}
                                                                            checked={item.checked}
                                                                            onChange={() => changeSearchFilterHandler(item, "user")}
                                                                        />
                                                                    }
                                                                />
                                                            )
                                                        })
                                                }

                                            </div>
                                        </AccordionDetails>
                                    </Accordion>

                                    <Accordion className="accordion-search-filter">
                                        <AccordionSummary
                                            expandIcon={<IonIcon icon={caretDown} className="text-[18px] text-black" />}
                                            aria-controls="panel1a-content"
                                            id="panel1a-header"
                                        >
                                            <p className="font-semibold text-mid">Sucursales</p>
                                        </AccordionSummary>

                                        <AccordionDetails>
                                            <div className="flex flex-col">
                                                {
                                                    searchFilters.sucursal.length === 0 ?
                                                        <p className="font-semibold text-primary">No hay datos disponibles</p>
                                                        :
                                                        searchFilters.sucursal.map((item: any) => {
                                                            return (
                                                                <FormControlLabel
                                                                    key={item.key}
                                                                    label={item.name}
                                                                    control=
                                                                    {
                                                                        <Checkbox
                                                                            id={item.key}
                                                                            name={item.name}
                                                                            value={item.key}
                                                                            checked={item.checked}
                                                                            onChange={() => changeSearchFilterHandler(item, "sucursal")}
                                                                        />
                                                                    }
                                                                />
                                                            )
                                                        }
                                                        )
                                                }
                                            </div>
                                        </AccordionDetails>
                                    </Accordion>
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </IonContent>
            {
                showImageModal &&
                <>
                    <ModalBackground />
                    <ImageVisualizer imageArray={imagesToShow} imageAlt="prueba" clickOutsideHandler={() => setShowImageModal(false)} />
                </>
            }
        </IonPage>
    )
}